import { atom } from 'jotai';
import { atomFamily, loadable } from 'jotai/utils';
import request from 'lib/request';
import type { WellSpacingJobStatusResponse, WellSpacingsData } from './types';

// Column ids the backend can sort on (see SORT_COLUMN_SQL in
// Query::WellSpacingJobStatuses). Anything outside this set is ignored so the
// server falls back to its default newest-first order.
const SORTABLE_COLUMN_IDS = new Set([
  'jobId',
  'triggerWellboreUwi14',
  'triggerType',
  'workflowStatus',
  'searchAreaFt',
  'durationSeconds',
  'createdAt',
  'completedAt',
]);

export interface SortState {
  id: string;
  desc: boolean;
}

export interface ColumnFilter {
  id: string;
  value: unknown;
}

// Filter atoms
export const hoursAtom = atom(24);
export const statusFilterAtom = atom<string>('all');
export const triggerTypeFilterAtom = atom<string>('all');

// Server-driven table state fed from the DataTable's onSortChange /
// onFilterChange / onGlobalSearchChange callbacks (see page.tsx handleLoadData).
export const textAtom = atom<string>('');
export const sortAtom = atom<SortState | null>(null);
export const columnFiltersAtom = atom<ColumnFilter[]>([]);

// Pagination atoms
export const pageAtom = atom(1);
export const pageSizeAtom = atom(100);

// Refresh trigger atom - increment to refetch data. Shared between the main
// list atom and the per-request sub-table atoms so that a single refetch()
// refreshes both when polling detects changes.
const refreshCounterAtom = atom(0);

// Shared AbortController — cancelled on each new fetch so stale requests
// don't overwrite fresh results.
let activeController = new AbortController();

// Base async atom that depends on filter atoms and refresh counter
const wellSpacingJobStatusBaseAtom = atom(async (get) => {
  get(refreshCounterAtom);

  return await fetchJobStatus({
    hours: get(hoursAtom),
    statusFilter: get(statusFilterAtom),
    triggerTypeFilter: get(triggerTypeFilterAtom),
    text: get(textAtom),
    sort: get(sortAtom),
    columnFilters: get(columnFiltersAtom),
    page: get(pageAtom),
    pageSize: get(pageSizeAtom),
  });
});

// Loadable wrapper for loading/error states
const wellSpacingJobStatusLoadableAtom = loadable(wellSpacingJobStatusBaseAtom);

// Combined atom that provides loadable state and refetch capability
export const wellSpacingJobStatusAtom = atom(
  (get) => get(wellSpacingJobStatusLoadableAtom),
  (_get, set) => {
    set(refreshCounterAtom, (c) => c + 1);
  }
);

export interface JobStatusQuery {
  hours: number;
  statusFilter: string;
  triggerTypeFilter: string;
  text: string;
  sort: SortState | null;
  columnFilters: ColumnFilter[];
  page: number;
  pageSize: number;
}

// Translate the DataTable's per-column filter state into the backend query
// params it understands. Columns without a server-side filter are ignored.
function applyColumnFilters(
  query: Record<string, unknown>,
  columnFilters: ColumnFilter[]
): void {
  for (const { id, value } of columnFilters) {
    if (id === 'wellName' && typeof value === 'string' && value.trim()) {
      query.well_name = value.trim();
    } else if (
      id === 'triggerWellboreUwi14' &&
      typeof value === 'string' &&
      value.trim()
    ) {
      query.trigger_uwi14 = value.trim();
    } else if (id === 'searchAreaFt' && Array.isArray(value)) {
      const [min, max] = value as [number | null, number | null];
      if (min !== null && min !== undefined) query.search_area_min = min;
      if (max !== null && max !== undefined) query.search_area_max = max;
    }
  }
}

// Shared fetch function used by both initial load and polling
export async function fetchJobStatus({
  hours,
  statusFilter,
  triggerTypeFilter,
  text,
  sort,
  columnFilters,
  page,
  pageSize,
}: JobStatusQuery): Promise<WellSpacingJobStatusResponse> {
  // Abort any in-flight request before starting a new one
  activeController.abort();
  activeController = new AbortController();

  const query: Record<string, unknown> = {
    hours,
    page,
    page_size: pageSize,
  };
  if (statusFilter !== 'all') query.status = statusFilter;
  if (triggerTypeFilter !== 'all') query.trigger_type = triggerTypeFilter;
  const trimmedText = text.trim();
  if (trimmedText) query.text = trimmedText;
  if (sort && SORTABLE_COLUMN_IDS.has(sort.id)) {
    query.sort_attribute = sort.id;
    query.sort_direction = sort.desc ? 'desc' : 'asc';
  }
  applyColumnFilters(query, columnFilters);

  const response = await request.post<WellSpacingJobStatusResponse, unknown>(
    '/queries/well_spacing_job_statuses',
    { query },
    activeController
  );

  if (response.type === 'abort') throw new Error('Request aborted');
  if (response.type !== 'success') {
    throw new Error(
      response.type === 'fatal' ? response.message : 'Request failed'
    );
  }
  return response.data;
}

// Build a fingerprint string from job statuses for cheap comparison.
// Only includes fields that change during processing — avoids
// unnecessary re-renders when the data hasn't actually changed.
export function buildFingerprint(data: WellSpacingJobStatusResponse): string {
  return data.results
    .map(
      (j) =>
        `${j.id}:${j.workflowStatus}:${j.validationStatus}:${j.offsetWellsCount}:${j.durationSeconds}`
    )
    .join('|');
}

// Per-request pagination atoms — trigger and offset sub-tables paginate
// independently, so each has its own page/pageSize state keyed by requestId.
// The `requestId` parameter is required by atomFamily for keying but isn't
// referenced in the atom's initial value.
/* eslint-disable @typescript-eslint/no-unused-vars */
export const triggerPageAtomFamily = atomFamily((requestId: number) => atom(1));
export const triggerPageSizeAtomFamily = atomFamily((requestId: number) =>
  atom(100)
);
export const offsetPageAtomFamily = atomFamily((requestId: number) => atom(1));
export const offsetPageSizeAtomFamily = atomFamily((requestId: number) =>
  atom(100)
);
/* eslint-enable @typescript-eslint/no-unused-vars */

// Well spacings for expandable row detail, keyed by requestId. Reads
// refreshCounterAtom so that whenever the main list refetches (via polling or
// manual refresh), currently-expanded sub-tables re-fetch alongside it.
// Collapsed rows have no active subscription and are skipped for free.
export const wellSpacingDetailsAtom = atomFamily((requestId: number) =>
  loadable(
    atom(async (get) => {
      get(refreshCounterAtom);
      const page = get(triggerPageAtomFamily(requestId));
      const pageSize = get(triggerPageSizeAtomFamily(requestId));
      const offsetPage = get(offsetPageAtomFamily(requestId));
      const offsetPageSize = get(offsetPageSizeAtomFamily(requestId));
      const controller = new AbortController();
      const response = await request.post<WellSpacingsData, unknown>(
        '/queries/well_spacing_job_details',
        {
          query: {
            request_id: requestId,
            page,
            page_size: pageSize,
            offset_page: offsetPage,
            offset_page_size: offsetPageSize,
          },
        },
        controller
      );

      if (response.type === 'abort') throw new Error('Request aborted');
      if (response.type !== 'success') {
        throw new Error(
          response.type === 'fatal' ? response.message : 'Request failed'
        );
      }
      return response.data;
    })
  )
);
